/**
 * Lost & Found Cats Pages
 * Public page for reporting and viewing lost/found cats
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, MapPin, Phone, Mail, User, Calendar, Gift, Cat,
  Plus, Filter, CheckCircle, AlertTriangle, Eye
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

import { API } from "../lib/api";

export const LostFoundPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showReportForm, setShowReportForm] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    report_type: "lost",
    cat_name: "",
    description: "",
    last_seen_location: "",
    last_seen_date: "",
    reward_offered: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    reporter_email: ""
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    try {
      let url = `${API}/lost-found`;
      if (filter === "lost") url += "?type=lost";
      else if (filter === "found") url += "?type=found";
      
      const res = await axios.get(url);
      setReports(res.data);
    } catch (err) {
      console.error("Failed to load reports:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cat_name || !formData.description || !formData.contact_email) {
      toast.error("Please fill in required fields (Cat name, description, and contact email)");
      return;
    }
    
    setSubmitting(true);
    try {
      // Create report
      const res = await axios.post(`${API}/lost-found`, formData);
      const reportId = res.data.report_id;
      
      // Upload image if provided
      if (imageFile && reportId) {
        const imageFormData = new FormData();
        imageFormData.append("file", imageFile);
        await axios.post(`${API}/lost-found/${reportId}/image`, imageFormData);
      }
      
      toast.success("Report submitted successfully! We hope your cat is found soon.");
      setShowReportForm(false);
      resetForm();
      loadReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      report_type: "lost",
      cat_name: "",
      description: "",
      last_seen_location: "",
      last_seen_date: "",
      reward_offered: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      reporter_email: ""
    });
    setImageFile(null);
    setImagePreview(null);
  };

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="lost-found-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Search className="w-4 h-4" />
            Lost & Found
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4 font-serif">
            Lost & Found Cats
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Help reunite lost cats with their families. Report a lost or found cat, 
            or browse listings to help bring cats home.
          </p>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex gap-2">
            <Button 
              variant={filter === "all" ? "default" : "outline"}
              onClick={() => setFilter("all")}
              className={filter === "all" ? "bg-amber-600 hover:bg-amber-700" : ""}
            >
              All Reports
            </Button>
            <Button 
              variant={filter === "lost" ? "default" : "outline"}
              onClick={() => setFilter("lost")}
              className={filter === "lost" ? "bg-red-600 hover:bg-red-700" : ""}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Lost Cats
            </Button>
            <Button 
              variant={filter === "found" ? "default" : "outline"}
              onClick={() => setFilter("found")}
              className={filter === "found" ? "bg-green-600 hover:bg-green-700" : ""}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Found Cats
            </Button>
          </div>
          
          <Button 
            onClick={() => setShowReportForm(true)}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Report a Cat
          </Button>
        </div>

        {/* Reports Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : reports.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Cat className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-700 mb-2">No Reports Yet</h3>
              <p className="text-stone-500 mb-4">
                {filter === "lost" 
                  ? "No lost cat reports at the moment. We hope it stays that way!"
                  : filter === "found"
                  ? "No found cat reports yet."
                  : "No lost or found cat reports have been submitted yet."}
              </p>
              <Button onClick={() => setShowReportForm(true)} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Submit a Report
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map(report => (
              <Card 
                key={report.report_id} 
                className={`overflow-hidden cursor-pointer hover:shadow-lg transition-shadow ${
                  report.report_type === "lost" ? "border-l-4 border-l-red-500" : "border-l-4 border-l-green-500"
                }`}
                onClick={() => setViewingReport(report)}
              >
                <div className="aspect-video relative bg-stone-100">
                  {report.image_base64 ? (
                    <img 
                      src={`data:${report.image_mime_type || 'image/jpeg'};base64,${report.image_base64}`}
                      alt={report.cat_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Cat className="w-16 h-16 text-stone-300" />
                    </div>
                  )}
                  <Badge 
                    className={`absolute top-3 left-3 ${
                      report.report_type === "lost" 
                        ? "bg-red-600 text-white" 
                        : "bg-green-600 text-white"
                    }`}
                  >
                    {report.report_type === "lost" ? "LOST" : "FOUND"}
                  </Badge>
                  {report.reward_offered && (
                    <Badge className="absolute top-3 right-3 bg-amber-500 text-white">
                      <Gift className="w-3 h-3 mr-1" />
                      Reward
                    </Badge>
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-xl text-stone-900 mb-2">{report.cat_name || "Unknown"}</h3>
                  <p className="text-stone-600 text-sm line-clamp-2 mb-3">{report.description}</p>
                  
                  {report.last_seen_location && (
                    <div className="flex items-center gap-2 text-sm text-stone-500 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{report.last_seen_location}</span>
                    </div>
                  )}
                  
                  {report.last_seen_date && (
                    <div className="flex items-center gap-2 text-sm text-stone-500 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(report.last_seen_date).toLocaleDateString('en-GB')}</span>
                    </div>
                  )}
                  
                  {report.reward_offered && (
                    <div className="mt-3 p-2 bg-amber-50 rounded-lg">
                      <p className="text-sm font-semibold text-amber-700">
                        <Gift className="w-4 h-4 inline mr-1" />
                        Reward: {report.reward_offered}
                      </p>
                    </div>
                  )}
                  
                  <Button variant="outline" className="w-full mt-4" size="sm">
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Report Form Dialog */}
        <Dialog open={showReportForm} onOpenChange={setShowReportForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Report a Lost or Found Cat</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 py-4">
              {/* Report Type */}
              <div className="grid grid-cols-2 gap-4">
                <Button
                  type="button"
                  variant={formData.report_type === "lost" ? "default" : "outline"}
                  className={`h-20 ${formData.report_type === "lost" ? "bg-red-600 hover:bg-red-700" : ""}`}
                  onClick={() => setFormData({...formData, report_type: "lost"})}
                >
                  <div className="text-center">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-1" />
                    <span>I Lost a Cat</span>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={formData.report_type === "found" ? "default" : "outline"}
                  className={`h-20 ${formData.report_type === "found" ? "bg-green-600 hover:bg-green-700" : ""}`}
                  onClick={() => setFormData({...formData, report_type: "found"})}
                >
                  <div className="text-center">
                    <CheckCircle className="w-6 h-6 mx-auto mb-1" />
                    <span>I Found a Cat</span>
                  </div>
                </Button>
              </div>

              {/* Cat Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-900 border-b pb-2">Cat Details</h3>
                
                <div>
                  <Label>Cat's Name *</Label>
                  <Input 
                    value={formData.cat_name}
                    onChange={e => setFormData({...formData, cat_name: e.target.value})}
                    placeholder={formData.report_type === "lost" ? "Your cat's name" : "Name if known, or 'Unknown'"}
                    required
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe the cat: color, breed, size, distinctive markings, collar, etc."
                    rows={4}
                    required
                  />
                </div>
                
                <div>
                  <Label>Photo of the Cat</Label>
                  <Input 
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="cursor-pointer"
                  />
                  {imagePreview && (
                    <div className="mt-2">
                      <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                    </div>
                  )}
                </div>
              </div>

              {/* Location & Date */}
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-900 border-b pb-2">Location & Date</h3>
                
                <div>
                  <Label>
                    {formData.report_type === "lost" ? "Last Seen Location" : "Found Location"}
                  </Label>
                  <Input 
                    value={formData.last_seen_location}
                    onChange={e => setFormData({...formData, last_seen_location: e.target.value})}
                    placeholder="Street, area, or landmarks near Norwich"
                  />
                </div>
                
                <div>
                  <Label>
                    {formData.report_type === "lost" ? "Date Last Seen" : "Date Found"}
                  </Label>
                  <Input 
                    type="date"
                    value={formData.last_seen_date}
                    onChange={e => setFormData({...formData, last_seen_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Reward (only for lost cats) */}
              {formData.report_type === "lost" && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-stone-900 border-b pb-2">Reward (Optional)</h3>
                  <div>
                    <Label>Reward Offered</Label>
                    <Input 
                      value={formData.reward_offered}
                      onChange={e => setFormData({...formData, reward_offered: e.target.value})}
                      placeholder="e.g., £50 reward for safe return"
                    />
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-900 border-b pb-2">Contact Information</h3>
                
                <div>
                  <Label>Your Name</Label>
                  <Input 
                    value={formData.contact_name}
                    onChange={e => setFormData({...formData, contact_name: e.target.value})}
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <Label>Contact Email *</Label>
                  <Input 
                    type="email"
                    value={formData.contact_email}
                    onChange={e => setFormData({...formData, contact_email: e.target.value})}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                
                <div>
                  <Label>Contact Phone (Optional)</Label>
                  <Input 
                    type="tel"
                    value={formData.contact_phone}
                    onChange={e => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowReportForm(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className={formData.report_type === "lost" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
                >
                  {submitting ? "Submitting..." : "Submit Report"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog open={!!viewingReport} onOpenChange={() => setViewingReport(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {viewingReport && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Badge className={viewingReport.report_type === "lost" ? "bg-red-600" : "bg-green-600"}>
                      {viewingReport.report_type === "lost" ? "LOST" : "FOUND"}
                    </Badge>
                    {viewingReport.cat_name}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Image */}
                  {viewingReport.image_base64 && (
                    <div className="aspect-video bg-stone-100 rounded-lg overflow-hidden">
                      <img 
                        src={`data:${viewingReport.image_mime_type || 'image/jpeg'};base64,${viewingReport.image_base64}`}
                        alt={viewingReport.cat_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Description */}
                  <div>
                    <h4 className="font-semibold text-stone-900 mb-2">Description</h4>
                    <p className="text-stone-600">{viewingReport.description}</p>
                  </div>
                  
                  {/* Details */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {viewingReport.last_seen_location && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-stone-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-stone-500">
                            {viewingReport.report_type === "lost" ? "Last Seen" : "Found At"}
                          </p>
                          <p className="text-stone-900">{viewingReport.last_seen_location}</p>
                        </div>
                      </div>
                    )}
                    
                    {viewingReport.last_seen_date && (
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-stone-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-stone-500">Date</p>
                          <p className="text-stone-900">
                            {new Date(viewingReport.last_seen_date).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'long', year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Reward */}
                  {viewingReport.reward_offered && (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-700 font-semibold">
                        <Gift className="w-5 h-5" />
                        Reward Offered
                      </div>
                      <p className="text-amber-800 mt-1">{viewingReport.reward_offered}</p>
                    </div>
                  )}
                  
                  {/* Contact Info */}
                  <div className="p-4 bg-stone-100 rounded-lg">
                    <h4 className="font-semibold text-stone-900 mb-3">Contact Information</h4>
                    <div className="space-y-2">
                      {viewingReport.contact_name && (
                        <div className="flex items-center gap-2 text-stone-600">
                          <User className="w-4 h-4" />
                          {viewingReport.contact_name}
                        </div>
                      )}
                      {viewingReport.contact_email && (
                        <div className="flex items-center gap-2 text-stone-600">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${viewingReport.contact_email}`} className="text-amber-600 hover:underline">
                            {viewingReport.contact_email}
                          </a>
                        </div>
                      )}
                      {viewingReport.contact_phone && (
                        <div className="flex items-center gap-2 text-stone-600">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${viewingReport.contact_phone}`} className="text-amber-600 hover:underline">
                            {viewingReport.contact_phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-stone-500 text-center">
                    Reported on {new Date(viewingReport.created_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setViewingReport(null)}>Close</Button>
                  {viewingReport.contact_email && (
                    <Button asChild className="bg-amber-600 hover:bg-amber-700">
                      <a href={`mailto:${viewingReport.contact_email}?subject=Re: ${viewingReport.report_type === "lost" ? "Lost" : "Found"} Cat - ${viewingReport.cat_name}`}>
                        <Mail className="w-4 h-4 mr-2" />
                        Contact Reporter
                      </a>
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LostFoundPage;
